/**
 * Sprint 24.5 — InstallStepUrlInput (Custom in-house MCP, DEV ONLY).
 *
 * Collects the MCP server URL for the dev escape-valve template. The connection
 * worker runs on Cloudflare and `connections/add` requires HTTPS, so the URL must
 * be an https endpoint (a tunnelled localhost or a dev MCP) — we validate that
 * here before advancing to the test step.
 */

import type { CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";

export interface InstallStepUrlInputProps {
  template: FirstPartyTemplate;
  url: string;
  onUrlChange: (url: string) => void;
  onContinue: () => void;
  onCancel: () => void;
}

export function InstallStepUrlInput({
  template,
  url,
  onUrlChange,
  onContinue,
  onCancel,
}: InstallStepUrlInputProps) {
  const valid = /^https:\/\/\S+$/i.test(url.trim());

  const avatar: CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: "var(--border-radius-md)",
    background: template.brand.avatar_bg,
    color: template.brand.avatar_color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    flexShrink: 0,
  };

  return (
    <div style={body}>
      <div style={headerRow}>
        <span style={avatar}>
          <i className={`ti ti-${template.brand.icon_name}`} aria-hidden="true" />
        </span>
        <h3 style={h3}>Register {template.display_name}</h3>
      </div>

      <label style={field}>
        <span style={labelText}>MCP server URL</span>
        <input
          style={input}
          type="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://your-mcp.example.com/mcp"
          autoFocus
        />
      </label>
      <div style={hint}>
        Must be an HTTPS endpoint reachable from Kuvio (tunnel localhost, or point at a dev MCP).
      </div>

      <div style={footer}>
        <button type="button" style={cancelLink} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={primaryBtn} onClick={onContinue} disabled={!valid}>
          Continue →
        </button>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const headerRow: CSSProperties = { display: "flex", alignItems: "center", gap: 10 };
const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };

const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelText: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)" };
const input: CSSProperties = {
  fontSize: 13,
  padding: "8px 10px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-mono)",
};

const hint: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5 };

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 4,
};

const cancelLink: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  opacity: 1,
};
